import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  ImagePlus,
  RotateCcw,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  mockupGarments,
  findMockupGarment,
  findMockupView,
  MockupView,
} from "../data/mockupGarments";

type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type LoadedImageInfo = {
  src: string;
  width: number;
  height: number;
};

type PrintableAreaWithUnit = {
  x: number;
  y: number;
  width: number;
  height: number;
  unit?: "px" | "percent";
};

type CurrentViewConfig = {
  printableArea?: PrintableAreaWithUnit;
  centerOffsetX?: number;
  centerOffsetY?: number;
  defaultOffsetX?: number;
  defaultOffsetY?: number;
  previewOnly?: boolean;
};

type GarmentColorOption = {
  label: string;
  value: string;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const DEFAULT_SCALE = 1;
const DEFAULT_ROTATION = 0;
const FALLBACK_DEFAULT_OFFSET_X = 0;
const FALLBACK_DEFAULT_OFFSET_Y = -8;
const DEFAULT_GARMENT_COLOR = "#ffffff";

const GARMENT_COLORS: GarmentColorOption[] = [
  { label: "White", value: "#ffffff" },
  { label: "Black", value: "#111111" },
  { label: "Athletic Gray", value: "#9ca3af" },
  { label: "Charcoal", value: "#4b5563" },
  { label: "Sand", value: "#d6c29c" },
  { label: "Cream", value: "#f1e7d0" },
  { label: "Red", value: "#c81e1e" },
  { label: "Maroon", value: "#6b1220" },
  { label: "Orange", value: "#ea580c" },
  { label: "Gold", value: "#d4a017" },
  { label: "Yellow", value: "#facc15" },
  { label: "Kelly Green", value: "#16a34a" },
  { label: "Forest Green", value: "#166534" },
  { label: "Teal", value: "#0f766e" },
  { label: "Light Blue", value: "#60a5fa" },
  { label: "Royal Blue", value: "#2563eb" },
  { label: "Navy", value: "#1e3a8a" },
  { label: "Purple", value: "#7c3aed" },
  { label: "Pink", value: "#ec4899" },
  { label: "Brown", value: "#6f4e37" },
];

const PREVIEW_STAGE_STYLE: React.CSSProperties = {
  backgroundColor: "#b8bec7",
  backgroundImage: `
    linear-gradient(45deg, rgba(255,255,255,0.18) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255,255,255,0.18) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.06) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.06) 75%)
  `,
  backgroundSize: "28px 28px",
  backgroundPosition: "0 0, 0 14px, 14px -14px, -14px 0px",
};

const getContainRect = (
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number
): Rect => {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0
  ) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const imageRatio = imageWidth / imageHeight;
  const containerRatio = containerWidth / containerHeight;

  let width = 0;
  let height = 0;

  if (imageRatio > containerRatio) {
    width = containerWidth;
    height = width / imageRatio;
  } else {
    height = containerHeight;
    width = height * imageRatio;
  }

  return {
    x: (containerWidth - width) / 2,
    y: (containerHeight - height) / 2,
    width,
    height,
  };
};

const resolvePrintableRect = (
  printableArea: PrintableAreaWithUnit,
  targetWidth: number,
  targetHeight: number,
  naturalWidth: number,
  naturalHeight: number
): Rect => {
  const unit = printableArea.unit ?? "px";

  if (unit === "percent") {
    return {
      x: printableArea.x * targetWidth,
      y: printableArea.y * targetHeight,
      width: printableArea.width * targetWidth,
      height: printableArea.height * targetHeight,
    };
  }

  const scaleX = naturalWidth > 0 ? targetWidth / naturalWidth : 1;
  const scaleY = naturalHeight > 0 ? targetHeight / naturalHeight : 1;

  return {
    x: printableArea.x * scaleX,
    y: printableArea.y * scaleY,
    width: printableArea.width * scaleX,
    height: printableArea.height * scaleY,
  };
};

const getArtworkRect = (
  printableRect: Rect,
  artworkAspectRatio: number,
  scale: number,
  offsetXPct: number,
  offsetYPct: number,
  centerOffsetXPct = 0,
  centerOffsetYPct = 0
): Rect => {
  if (
    printableRect.width <= 0 ||
    printableRect.height <= 0 ||
    artworkAspectRatio <= 0
  ) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const maxBaseWidth = printableRect.width * 0.8;
  const maxBaseHeight = printableRect.height * 0.7;

  let baseWidth = maxBaseWidth;
  let baseHeight = baseWidth / artworkAspectRatio;

  if (baseHeight > maxBaseHeight) {
    baseHeight = maxBaseHeight;
    baseWidth = baseHeight * artworkAspectRatio;
  }

  const width = baseWidth * scale;
  const height = baseHeight * scale;

  const centeredX =
    printableRect.x +
    (printableRect.width - width) / 2 +
    printableRect.width * (centerOffsetXPct / 100);

  const centeredY =
    printableRect.y +
    (printableRect.height - height) / 2 +
    printableRect.height * (centerOffsetYPct / 100);

  const x = centeredX + printableRect.width * (offsetXPct / 100);
  const y = centeredY + printableRect.height * (offsetYPct / 100);

  return { x, y, width, height };
};

const getBaseArtworkSize = (
  printableRect: Rect,
  artworkAspectRatio: number
): { width: number; height: number } => {
  const maxBaseWidth = printableRect.width * 0.8;
  const maxBaseHeight = printableRect.height * 0.7;

  let baseWidth = maxBaseWidth;
  let baseHeight = baseWidth / artworkAspectRatio;

  if (baseHeight > maxBaseHeight) {
    baseHeight = maxBaseHeight;
    baseWidth = baseHeight * artworkAspectRatio;
  }

  return {
    width: baseWidth,
    height: baseHeight,
  };
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const drawTintedGarment = (
  ctx: CanvasRenderingContext2D,
  baseImage: HTMLImageElement,
  garmentColor: string,
  width: number,
  height: number
) => {
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(baseImage, 0, 0, width, height);

  if (garmentColor.toLowerCase() === "#ffffff") {
    return;
  }

  const tintCanvas = document.createElement("canvas");
  tintCanvas.width = width;
  tintCanvas.height = height;

  const tintCtx = tintCanvas.getContext("2d");
  if (!tintCtx) return;

  tintCtx.clearRect(0, 0, width, height);
  tintCtx.drawImage(baseImage, 0, 0, width, height);
  tintCtx.globalCompositeOperation = "source-atop";
  tintCtx.fillStyle = garmentColor;
  tintCtx.fillRect(0, 0, width, height);

  ctx.globalCompositeOperation = "multiply";
  ctx.drawImage(tintCanvas, 0, 0, width, height);
  ctx.globalCompositeOperation = "source-over";
};

type PlacementControlsProps = {
  scale: number;
  rotation: number;
  offsetX: number;
  offsetY: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  setRotation: React.Dispatch<React.SetStateAction<number>>;
  setOffsetX: React.Dispatch<React.SetStateAction<number>>;
  setOffsetY: React.Dispatch<React.SetStateAction<number>>;
  artworkUrl: string | null;
};

function PlacementControls({
  scale,
  rotation,
  offsetX,
  offsetY,
  setScale,
  setRotation,
  setOffsetX,
  setOffsetY,
  artworkUrl,
}: PlacementControlsProps) {
  const horizontalLabel = `${offsetX.toFixed(0)}%`;
  const verticalLabel = `${offsetY.toFixed(0)}%`;
  const rotationLabel = `${rotation.toFixed(0)}°`;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#08111f] p-4">
      <p className="text-sm font-bold text-white">Placement Controls</p>

      <div className="mt-4 space-y-4">
        <div>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-dtf-text-muted">Scale</span>
            <span className="text-white">{scale.toFixed(2)}x</span>
          </div>
          <input
            type="range"
            min="0.2"
            max="2.5"
            step="0.01"
            value={scale}
            onChange={(e) =>
              setScale(clamp(Number(e.target.value), 0.2, 2.5))
            }
            className="w-full"
            disabled={!artworkUrl}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-dtf-text-muted">Rotation</span>
            <span className="text-white">{rotationLabel}</span>
          </div>
          <input
            type="range"
            min="-180"
            max="180"
            step="1"
            value={rotation}
            onChange={(e) =>
              setRotation(clamp(Number(e.target.value), -180, 180))
            }
            className="w-full"
            disabled={!artworkUrl}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-dtf-text-muted">Horizontal</span>
            <span className="text-white">{horizontalLabel}</span>
          </div>
          <input
            type="range"
            min="-50"
            max="50"
            step="1"
            value={offsetX}
            onChange={(e) =>
              setOffsetX(clamp(Number(e.target.value), -50, 50))
            }
            className="w-full"
            disabled={!artworkUrl}
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-dtf-text-muted">Vertical</span>
            <span className="text-white">{verticalLabel}</span>
          </div>
          <input
            type="range"
            min="-50"
            max="50"
            step="1"
            value={offsetY}
            onChange={(e) =>
              setOffsetY(clamp(Number(e.target.value), -50, 50))
            }
            className="w-full"
            disabled={!artworkUrl}
          />
        </div>

        {!artworkUrl && (
          <p className="text-xs text-dtf-text-muted">
            Upload artwork to enable placement controls.
          </p>
        )}
      </div>
    </div>
  );
}

export default function MockupBuilderPage() {
  const [garmentId, setGarmentId] = useState("adult-tee");
  const [view, setView] = useState<MockupView>("front");
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null);
  const [garmentColor, setGarmentColor] = useState(DEFAULT_GARMENT_COLOR);

  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [rotation, setRotation] = useState(DEFAULT_ROTATION);
  const [offsetX, setOffsetX] = useState(FALLBACK_DEFAULT_OFFSET_X);
  const [offsetY, setOffsetY] = useState(FALLBACK_DEFAULT_OFFSET_Y);

  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
  const [mockupImageInfo, setMockupImageInfo] = useState<LoadedImageInfo | null>(
    null
  );
  const [artworkImageInfo, setArtworkImageInfo] =
    useState<LoadedImageInfo | null>(null);
  const [isDraggingArtwork, setIsDraggingArtwork] = useState(false);

  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{
    pointerX: number;
    pointerY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);

  const garment = useMemo(() => findMockupGarment(garmentId), [garmentId]);
  const currentView = useMemo(
    () => findMockupView(garmentId, view),
    [garmentId, view]
  );

  const currentViewConfig = currentView as CurrentViewConfig | null;

  const printableArea = (currentViewConfig?.printableArea ||
    null) as PrintableAreaWithUnit | null;

  const centerOffsetX = currentViewConfig?.centerOffsetX ?? 0;
  const centerOffsetY = currentViewConfig?.centerOffsetY ?? 0;
  const defaultOffsetX =
    currentViewConfig?.defaultOffsetX ?? FALLBACK_DEFAULT_OFFSET_X;
  const defaultOffsetY =
    currentViewConfig?.defaultOffsetY ?? FALLBACK_DEFAULT_OFFSET_Y;

  const applyDefaultPlacement = () => {
    setScale(DEFAULT_SCALE);
    setRotation(DEFAULT_ROTATION);
    setOffsetX(defaultOffsetX);
    setOffsetY(defaultOffsetY);
  };

  useEffect(() => {
    if (!garment) return;

    if (!garment.views.find((item) => item.view === view)) {
      setView(garment.views[0]?.view || "front");
    }
  }, [garment, view]);

  useEffect(() => {
    return () => {
      if (artworkUrl) {
        URL.revokeObjectURL(artworkUrl);
      }
    };
  }, [artworkUrl]);

  useEffect(() => {
    if (!currentView?.image) {
      setMockupImageInfo(null);
      return;
    }

    let cancelled = false;

    loadImage(currentView.image)
      .then((image) => {
        if (cancelled) return;
        setMockupImageInfo({
          src: currentView.image,
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setMockupImageInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [currentView?.image]);

  useEffect(() => {
    if (!artworkUrl) {
      setArtworkImageInfo(null);
      return;
    }

    let cancelled = false;

    loadImage(artworkUrl)
      .then((image) => {
        if (cancelled) return;
        setArtworkImageInfo({
          src: artworkUrl,
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setArtworkImageInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [artworkUrl]);

  useEffect(() => {
    const element = previewFrameRef.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setPreviewSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateSize();

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  useEffect(() => {
    setScale(DEFAULT_SCALE);
    setRotation(DEFAULT_ROTATION);
    setOffsetX(defaultOffsetX);
    setOffsetY(defaultOffsetY);
  }, [garmentId, view, defaultOffsetX, defaultOffsetY]);

  const resetPlacement = () => {
    applyDefaultPlacement();
  };

  const handleArtworkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const nextUrl = URL.createObjectURL(file);

    setArtworkUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return nextUrl;
    });

    setScale(DEFAULT_SCALE);
    setRotation(DEFAULT_ROTATION);
    setOffsetX(defaultOffsetX);
    setOffsetY(defaultOffsetY);
  };

  const handleGarmentChange = (value: string) => {
    setGarmentId(value);

    const nextGarment = findMockupGarment(value);
    setView(nextGarment?.views[0]?.view || "front");
  };

  const removeArtwork = () => {
    setArtworkUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setArtworkImageInfo(null);
    setIsDraggingArtwork(false);
    applyDefaultPlacement();
  };

  const previewImageRect = useMemo(() => {
    if (!mockupImageInfo) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    return getContainRect(
      previewSize.width,
      previewSize.height,
      mockupImageInfo.width,
      mockupImageInfo.height
    );
  }, [mockupImageInfo, previewSize.height, previewSize.width]);

  const previewPrintableRect = useMemo(() => {
    if (!printableArea || !mockupImageInfo) return null;

    return resolvePrintableRect(
      printableArea,
      previewImageRect.width,
      previewImageRect.height,
      mockupImageInfo.width,
      mockupImageInfo.height
    );
  }, [printableArea, mockupImageInfo, previewImageRect]);

  const applyRecommendedPreset = () => {
    if (!currentView || !previewPrintableRect || !artworkImageInfo) {
      applyDefaultPlacement();
      return;
    }

    const targetFillByGarment: Record<string, number> = {
      "adult-tee": 0.85,
      hoodie: 0.9,
      crewneck: 0.9,
      longsleeve: 0.85,
      "youth-tee": 0.75,
    };

    const artworkAspectRatio =
      artworkImageInfo.width > 0 && artworkImageInfo.height > 0
        ? artworkImageInfo.width / artworkImageInfo.height
        : 1;

    const baseArtworkSize = getBaseArtworkSize(
      previewPrintableRect,
      artworkAspectRatio
    );

    if (baseArtworkSize.width <= 0) {
      applyDefaultPlacement();
      return;
    }

    const targetFill = targetFillByGarment[garmentId] ?? 0.85;
    const targetWidth = previewPrintableRect.width * targetFill;
    const nextScale = clamp(targetWidth / baseArtworkSize.width, 0.2, 2.5);

    setScale(nextScale);
    setRotation(DEFAULT_ROTATION);
    setOffsetX(defaultOffsetX);
    setOffsetY(defaultOffsetY);
  };

  const previewArtworkRect = useMemo(() => {
    if (!previewPrintableRect || !artworkImageInfo) return null;

    const artworkAspectRatio =
      artworkImageInfo.width > 0 && artworkImageInfo.height > 0
        ? artworkImageInfo.width / artworkImageInfo.height
        : 1;

    return getArtworkRect(
      previewPrintableRect,
      artworkAspectRatio,
      scale,
      offsetX,
      offsetY,
      centerOffsetX,
      centerOffsetY
    );
  }, [
    previewPrintableRect,
    artworkImageInfo,
    scale,
    offsetX,
    offsetY,
    centerOffsetX,
    centerOffsetY,
  ]);

  const startArtworkDrag = (clientX: number, clientY: number) => {
    if (!artworkUrl || !previewPrintableRect || !previewArtworkRect) return;

    dragStartRef.current = {
      pointerX: clientX,
      pointerY: clientY,
      startOffsetX: offsetX,
      startOffsetY: offsetY,
    };

    setIsDraggingArtwork(true);
  };

  const updateArtworkDrag = (clientX: number, clientY: number) => {
    if (!dragStartRef.current || !previewPrintableRect) return;

    const deltaX = clientX - dragStartRef.current.pointerX;
    const deltaY = clientY - dragStartRef.current.pointerY;

    const deltaXPct = (deltaX / previewPrintableRect.width) * 100;
    const deltaYPct = (deltaY / previewPrintableRect.height) * 100;

    setOffsetX(
      clamp(dragStartRef.current.startOffsetX + deltaXPct, -50, 50)
    );
    setOffsetY(
      clamp(dragStartRef.current.startOffsetY + deltaYPct, -50, 50)
    );
  };

  const endArtworkDrag = () => {
    dragStartRef.current = null;
    setIsDraggingArtwork(false);
  };

  useEffect(() => {
    if (!isDraggingArtwork) return;

    const handleMouseMove = (event: MouseEvent) => {
      updateArtworkDrag(event.clientX, event.clientY);
    };

    const handleMouseUp = () => {
      endArtworkDrag();
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!event.touches[0]) return;
      event.preventDefault();
      updateArtworkDrag(event.touches[0].clientX, event.touches[0].clientY);
    };

    const handleTouchEnd = () => {
      endArtworkDrag();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDraggingArtwork, previewPrintableRect]);

  const downloadMockup = async () => {
    if (!currentView?.image || !artworkUrl || !printableArea) return;

    try {
      const [mockupImage, artworkImage] = await Promise.all([
        loadImage(currentView.image),
        loadImage(artworkUrl),
      ]);

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = mockupImage.naturalWidth;
      exportCanvas.height = mockupImage.naturalHeight;

      const ctx = exportCanvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, exportCanvas.width, exportCanvas.height);
      drawTintedGarment(
        ctx,
        mockupImage,
        garmentColor,
        exportCanvas.width,
        exportCanvas.height
      );

      const exportPrintableRect = resolvePrintableRect(
        printableArea,
        exportCanvas.width,
        exportCanvas.height,
        mockupImage.naturalWidth,
        mockupImage.naturalHeight
      );

      const artworkAspectRatio =
        artworkImage.naturalWidth > 0 && artworkImage.naturalHeight > 0
          ? artworkImage.naturalWidth / artworkImage.naturalHeight
          : 1;

      const exportArtworkRect = getArtworkRect(
        exportPrintableRect,
        artworkAspectRatio,
        scale,
        offsetX,
        offsetY,
        centerOffsetX,
        centerOffsetY
      );

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.save();
      ctx.translate(
        exportArtworkRect.x + exportArtworkRect.width / 2,
        exportArtworkRect.y + exportArtworkRect.height / 2
      );
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(
        artworkImage,
        -exportArtworkRect.width / 2,
        -exportArtworkRect.height / 2,
        exportArtworkRect.width,
        exportArtworkRect.height
      );
      ctx.restore();

      exportCanvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `dtf-mockup-${garmentId}-${view}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (error) {
      console.error("Mockup export failed:", error);
    }
  };

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="mb-6">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <Sparkles className="h-4 w-4" />
            Mockup Tool
          </div>

          <h1 className="text-3xl font-black tracking-tight text-white md:text-4xl">
            DTF Mockup Builder
          </h1>

          <p className="mt-3 max-w-3xl text-sm leading-6 text-dtf-text-muted md:text-base">
            Upload artwork, preview placement, pick a garment color, and
            download a clean mockup without distortion.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
          <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-bold text-white">
                  Garment
                </label>
                <select
                  value={garmentId}
                  onChange={(e) => handleGarmentChange(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-[#08111f] px-4 py-3 text-white outline-none transition focus:border-dtf-blue/40"
                >
                  {mockupGarments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-white">
                  View
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(garment?.views || []).map((item) => (
                    <button
                      key={item.view}
                      type="button"
                      onClick={() => setView(item.view)}
                      className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition ${
                        view === item.view
                          ? "border-dtf-blue/30 bg-dtf-blue/10 text-dtf-blue"
                          : "border-white/10 bg-[#08111f] text-dtf-text-muted hover:border-dtf-blue/20 hover:text-white"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                {currentView?.previewOnly && (
                  <div className="mt-2 text-xs text-dtf-accent">
                    Preview only — placement is based on the main printable
                    views.
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-white">
                  Garment Color
                </label>

                <div className="grid grid-cols-5 gap-2">
                  {GARMENT_COLORS.map((color) => {
                    const isActive = garmentColor.toLowerCase() === color.value;

                    return (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setGarmentColor(color.value)}
                        className={`flex h-11 w-full items-center justify-center rounded-2xl border transition ${
                          isActive
                            ? "border-white shadow-[0_0_0_1px_rgba(255,255,255,0.55)]"
                            : "border-white/10 hover:border-dtf-blue/30"
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                        aria-label={color.label}
                      >
                        <span className="sr-only">{color.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#08111f] px-3 py-3">
                  <input
                    type="color"
                    value={garmentColor}
                    onChange={(e) => setGarmentColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border border-white/10 bg-transparent"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">
                      Custom Color
                    </p>
                    <p className="text-xs uppercase tracking-[0.18em] text-dtf-text-muted">
                      {garmentColor}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-white">
                  Artwork Upload
                </label>

                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 px-4 py-4 text-sm font-semibold text-dtf-blue transition hover:border-dtf-blue/35 hover:bg-dtf-blue/15">
                  <ImagePlus className="h-4 w-4" />
                  Upload Artwork
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleArtworkUpload}
                  />
                </label>
              </div>

              <div className="hidden lg:block">
                <PlacementControls
                  scale={scale}
                  rotation={rotation}
                  offsetX={offsetX}
                  offsetY={offsetY}
                  setScale={setScale}
                  setRotation={setRotation}
                  setOffsetX={setOffsetX}
                  setOffsetY={setOffsetY}
                  artworkUrl={artworkUrl}
                />
              </div>

              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={resetPlacement}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#08111f] px-4 py-3 text-sm font-semibold text-white transition hover:border-dtf-blue/20"
                >
                  <RotateCcw className="h-4 w-4 text-dtf-blue" />
                  Default Placement
                </button>

                <button
                  type="button"
                  onClick={() => setGarmentColor(DEFAULT_GARMENT_COLOR)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#08111f] px-4 py-3 text-sm font-semibold text-white transition hover:border-dtf-blue/20"
                >
                  Reset Garment Color
                </button>

                <button
                  type="button"
                  onClick={removeArtwork}
                  disabled={!artworkUrl}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    artworkUrl
                      ? "border-white/10 bg-[#08111f] text-white hover:border-dtf-blue/20"
                      : "cursor-not-allowed border-white/5 bg-[#08111f]/60 text-dtf-text-muted"
                  }`}
                >
                  <Trash2 className="h-4 w-4 text-dtf-blue" />
                  Remove Artwork
                </button>

                <button
                  type="button"
                  onClick={applyRecommendedPreset}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 px-4 py-3 text-sm font-semibold text-dtf-blue transition hover:border-dtf-blue/35 hover:bg-dtf-blue/15"
                >
                  Apply Recommended Size
                </button>

                <button
                  type="button"
                  onClick={downloadMockup}
                  disabled={!artworkUrl || !currentView || !printableArea}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    artworkUrl && currentView && printableArea
                      ? "border-dtf-accent/20 bg-dtf-accent/10 text-dtf-accent hover:border-dtf-accent/35 hover:bg-dtf-accent/15"
                      : "cursor-not-allowed border-white/5 bg-[#08111f]/60 text-dtf-text-muted"
                  }`}
                >
                  <Download className="h-4 w-4" />
                  Download Mockup
                </button>
              </div>

              <div className="rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 p-4">
                <p className="text-sm font-bold text-dtf-blue">
                  Local Processing
                </p>
                <p className="mt-1 text-sm leading-6 text-dtf-text">
                  Images stay in the browser. Nothing is uploaded to the server.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-lg font-extrabold text-white">
                  Live Mockup Preview
                </p>
                <p className="text-sm text-dtf-text-muted">
                  Preview artwork placement and garment color before export.
                </p>
              </div>

              {currentView && (
                <div className="rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-semibold text-dtf-blue">
                  {currentView.label}
                </div>
              )}
            </div>

            <div className="flex justify-center">
              <div className="w-full max-w-[920px]">
                <div className="grid gap-4 lg:mx-auto lg:block lg:max-w-[520px]">
                  <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-[#0d1320] p-4">
                    <div
                      ref={previewFrameRef}
                      className="relative mx-auto h-[420px] w-full overflow-hidden rounded-[20px] border border-black/10 sm:h-[520px] lg:h-[560px] lg:max-w-[460px]"
                      style={PREVIEW_STAGE_STYLE}
                    >
                      <div className="pointer-events-none absolute inset-0 bg-white/6" />

                      {currentView && mockupImageInfo ? (
                        <>
                          <img
                            src={currentView.image}
                            alt={`${garment?.label} ${currentView.label}`}
                            className="absolute inset-0 h-full w-full object-contain"
                            draggable={false}
                          />

                          {garmentColor !== "#ffffff" && (
                            <div
                              className="pointer-events-none absolute inset-0"
                              style={{
                                backgroundColor: garmentColor,
                                mixBlendMode: "multiply",
                                opacity: 0.9,
                                WebkitMaskImage: `url(${currentView.image})`,
                                maskImage: `url(${currentView.image})`,
                                WebkitMaskRepeat: "no-repeat",
                                maskRepeat: "no-repeat",
                                WebkitMaskPosition: "center",
                                maskPosition: "center",
                                WebkitMaskSize: "contain",
                                maskSize: "contain",
                              }}
                            />
                          )}

                          {artworkUrl && previewArtworkRect && (
                            <img
                              src={artworkUrl}
                              alt="Artwork preview"
                              draggable={false}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                startArtworkDrag(event.clientX, event.clientY);
                              }}
                              onTouchStart={(event) => {
                                if (!event.touches[0]) return;
                                startArtworkDrag(
                                  event.touches[0].clientX,
                                  event.touches[0].clientY
                                );
                              }}
                              className={`absolute object-contain ${
                                isDraggingArtwork ? "cursor-grabbing" : "cursor-grab"
                              }`}
                              style={{
                                left: `${previewImageRect.x + previewArtworkRect.x}px`,
                                top: `${previewImageRect.y + previewArtworkRect.y}px`,
                                width: `${previewArtworkRect.width}px`,
                                height: `${previewArtworkRect.height}px`,
                                transform: `rotate(${rotation}deg)`,
                                transformOrigin: "center center",
                                touchAction: "none",
                                userSelect: "none",
                                WebkitUserSelect: "none",
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-[#273244]">
                          No mockup view available.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="lg:hidden">
                    <PlacementControls
                      scale={scale}
                      rotation={rotation}
                      offsetX={offsetX}
                      offsetY={offsetY}
                      setScale={setScale}
                      setRotation={setRotation}
                      setOffsetX={setOffsetX}
                      setOffsetY={setOffsetY}
                      artworkUrl={artworkUrl}
                    />
                  </div>
                </div>
              </div>
            </div>

            {!artworkUrl && (
              <div className="mt-4 rounded-2xl border border-dashed border-white/10 bg-[#08111f] p-4 text-sm text-dtf-text-muted">
                Upload artwork to start building your mockup.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}